import type { QueryClient } from '@tanstack/react-query';
import type { LoaderFunctionArgs, ShouldRevalidateFunctionArgs } from 'react-router';

import { redirect } from 'react-router';

import {
  properties as propertiesQueryOptions,
  providerDirectory as providerDirectoryQueryOptions,
  savedViews as savedViewsQueryOptions,
} from '#lib/react-query/queries.ts';
import { redeemMagicToken } from '#services/api/auth.ts';
import { requireAuthorizedInsightsUser } from '#utils/auth.ts';
import { setAccessToken } from '#utils/cookies.ts';
import { allProvidersToken, displayedProvidersToken } from '#utils/provider-filter.ts';
import {
  getCoordinateAutoViewRedirectParams,
  toFeedFilters,
} from './Filters/CoordinateFilters.utils.ts';
import {
  getCanonicalProviderFilterRedirectParams,
  isDisplayedProvidersFilter,
  resolveProviderFilter,
  resolveProviderSeed,
  setProviderFilterSearchParams,
} from './Filters/Provider.utils.ts';
import {
  getParsedSearchParams,
  getReadinessMagicLinkSearchParams,
  getReadinessMagicLinkSearchParamsFromNextUrl,
  getSearchParamsToParse,
} from './Home.utils.ts';

const transferQueueProperty = 'settings.signal.transferQueue';

// Whether this loader run is the document's entry (first run after a full page load — a bookmark,
// magic link, or refresh) rather than an SPA navigation. Entry is when the Coordinate auto-view
// may attach itself as a ?view= token; later in-session bare URLs mean the user cleared their
// filters, and re-attaching would resurrect them.
let hasHandledDocumentEntry = false;

export const loader = (queryClient: QueryClient) => async (args: LoaderFunctionArgs) => {
  const url = new URL(args.request.url);
  await handleMiaMagicLink(queryClient, url);

  const me = await requireAuthorizedInsightsUser(queryClient);
  const [properties, providers, savedViewsData] = await Promise.all([
    queryClient.ensureQueryData(propertiesQueryOptions),
    queryClient.ensureQueryData(providerDirectoryQueryOptions),
    queryClient.ensureQueryData(savedViewsQueryOptions),
  ]);
  const searchParams = url.searchParams;
  const viewId = searchParams.get('view');
  const activeView = savedViewsData.views.find((view) => view.id === Number(viewId));
  const isTransferQueueEnabled = properties[transferQueueProperty] === 1;
  const requestedTab = activeView?.tabType ?? searchParams.get('tab');

  if (!isTransferQueueEnabled && requestedTab === 'transferQueue') {
    const redirectParams = new URLSearchParams(searchParams);
    redirectParams.set('tab', 'scheduling');
    redirectParams.delete('view');
    // oxlint-disable-next-line typescript/only-throw-error
    throw redirect(`${url.pathname}?${redirectParams.toString()}`);
  }

  if (!hasHandledDocumentEntry) {
    hasHandledDocumentEntry = true;
    const redirectParams = getCoordinateAutoViewRedirectParams(searchParams, savedViewsData.views);

    if (redirectParams) {
      // oxlint-disable-next-line typescript/only-throw-error
      throw redirect(`${url.pathname}?${redirectParams.toString()}`);
    }
  }

  const hasInvalidView = searchParams.has('view') && !activeView;

  if (!searchParams.has('pId') && !activeView) {
    const providerSeed = resolveProviderSeed({
      properties: properties,
      providers,
      user: me.user,
    });
    const hasProviderSeed = providerSeed === allProvidersToken || providerSeed.length > 0;

    if (hasProviderSeed || hasInvalidView) {
      const redirectParams = new URLSearchParams(searchParams);
      if (hasInvalidView) {
        redirectParams.delete('view');
      }
      if (hasProviderSeed) {
        setProviderFilterSearchParams(redirectParams, displayedProvidersToken);
      }

      // The PHP Insights endpoints treat an omitted provider filter as organization-wide, so the
      // seed must live in the URL rather than only in the UI to keep chips and requests aligned.
      // oxlint-disable-next-line typescript/only-throw-error
      throw redirect(`${url.pathname}?${redirectParams.toString()}`);
    }
  }

  if (hasInvalidView) {
    const redirectParams = new URLSearchParams(searchParams);
    redirectParams.delete('view');
    // oxlint-disable-next-line typescript/only-throw-error
    throw redirect(`${url.pathname}?${redirectParams.toString()}`);
  }

  const paramsToParse = getSearchParamsToParse(searchParams, savedViewsData);

  const parsed = getParsedSearchParams(paramsToParse);
  const resolvedProviderIds = resolveProviderFilter({
    searchParams: paramsToParse,
    providerFilter: parsed.providerIds,
    properties,
    providers,
    user: me.user,
  });
  const providerIds =
    activeView && activeView.filters.providerIds === undefined
      ? allProvidersToken
      : resolvedProviderIds;

  if (!activeView && searchParams.has('pId')) {
    const redirectParams = getCanonicalProviderFilterRedirectParams(searchParams, providerIds);
    if (redirectParams) {
      // oxlint-disable-next-line typescript/only-throw-error
      throw redirect(`${url.pathname}?${redirectParams.toString()}`);
    }
  }

  return {
    ...parsed,
    providerIds,
    // Derived here so the Coordinate tab's feed inputs are computed once per navigation
    // instead of on every render — and not at all for the tabs that can't use them.
    coordinateFeedFilters:
      parsed.tab === 'coordinate'
        ? toFeedFilters({
            ...parsed,
            providerIds,
            isDisplayedProvidersScope: isDisplayedProvidersFilter(paramsToParse),
          })
        : undefined,
    savedViewsData,
    activeView,
    isTransferQueueEnabled,
    me,
  };
};

export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
  // Remove trailing slashes to handle cases where the URL might have them
  const pathname = args.currentUrl.pathname.replace(/\/+$/, '');

  // Don't revalidate when drilldown routes are rendered, as they change search
  // params that are shared with the home route.
  if (pathname === '/appointments/drilldown' || pathname === '/forms/drilldown') {
    return false;
  }

  return args.defaultShouldRevalidate;
}

async function handleMiaMagicLink(queryClient: QueryClient, url: URL) {
  const magicToken = url.searchParams.get('mt');

  if (!magicToken) {
    return;
  }

  // BAD-433: Legacy Mia v0 links are Home-route-specific because Coordinate is a Home tab.
  // Move this to route-agnostic auth bootstrap when another magic-link flow exists.
  const result = await redeemMagicToken(magicToken);

  if (result.status === 'failed') {
    // oxlint-disable-next-line typescript/only-throw-error
    throw redirect('/unauthorized?reason=expired-link');
  }

  if (result.status === 'redeemed') {
    setAccessToken(result.jwt);
    queryClient.clear();

    const redirectSearchParams = getReadinessMagicLinkSearchParamsFromNextUrl({
      nextUrl: result.nextUrl,
      fallbackSearchParams: url.searchParams,
    });

    // oxlint-disable-next-line typescript/only-throw-error
    throw redirect(`${url.pathname}?${redirectSearchParams.toString()}`);
  }

  url.search = getReadinessMagicLinkSearchParams(url.searchParams).toString();

  // oxlint-disable-next-line typescript/only-throw-error
  throw redirect(`${url.pathname}${url.search}`);
}
